// The Offer Ladder's store: generic Supabase CRUD (recordStore) + the
// offer-specific logic that reads her locked My Offer (offerParse).
//
// Replaces the localStorage `offerStore.js`, which is now only kept as the
// migration source — see `migrateOffers`.
//
// The once-ever seed flag lives in `settings.offers_seeded`, NOT localStorage.
// That matters: if the offers are in the cloud and the flag is in the browser,
// a second browser re-runs the pull and creates a duplicate Signature Offer.
// App.jsx owns that column and passes it in, so this hook stays storage-agnostic
// about it.

import { useCallback, useEffect, useRef } from "react";
import { createRecordStore } from "./recordStore";
import {
  SOURCE_SECTION,
  DEFAULT_CORE_NAME,
  readSignatureOffer,
  hasSignatureOffer,
} from "./offerParse";

// `id`, `createdAt` and `updatedAt` are handled by recordStore — not listed here.
// `sortOrder` IS written now (2026-09-17, her ask: "offer items should be
// draggable to reorder") — by `reorderOffers` in MomentumHub, through
// `offerReorderWrites`. Every row saved before that holds 0, so `groupOffers`
// breaks the tie on created_at DESC, which is the order the ladder always
// showed; her first drag inside a rung renumbers the whole list. `tiedTo` is free text, not a foreign key — see the schema
// comment in supabase/0001_momentum_schema.sql for why. `ctaId` IS a real
// foreign key (supabase/0003_offer_cta.sql), so it must be a live `ctas.id` or
// null — never a leftover id from a CTA she has since deleted. `pageId` and
// `linkUrl` (supabase/0018) are the other two answers to "where does this offer
// send people"; exactly one of the three is set — see utils/offerDestination.js.
export const OFFER_FIELDS = [
  "type", "name", "summary", "pricing", "status", "tiedTo", "ctaId", "pageId", "linkUrl", "sortOrder",
  // `productType` (supabase/0026) is service | digital | physical, and what GHL
  // is told when an offer is pushed as a product. Null on every row written
  // before that migration — see productTypeOf, which reads null as service
  // without writing it.
  "productType",
  // `details` (supabase/0029) is "Tell Brill more": the long description, sent
  // to Brill by utils/offerLine.js and never to GHL. Undefined on every row
  // loaded before that migration runs, so toRow leaves it out and those saves
  // still work; only typing in the box needs the column.
  "details",
  "source", "sourceSection", "sourceText", "sourceHash",
];

const useOfferRecords = createRecordStore({
  table: "offers",
  fields: OFFER_FIELDS,
  idPrefix: "off",
});

/**
 * @param offerOutputs  her locked My Offer sections, from App's mergedOutputs
 * @param offersSeeded  settings.offers_seeded — whether the once-ever pull ran
 * @param markSeeded    async; persists offers_seeded = true
 * @param enabled       gate for the automatic pull. MUST stay false until the
 *                      localStorage migration has been attempted and settings
 *                      have loaded — otherwise the pull sees an empty table and a
 *                      false flag, and creates a duplicate of an offer she
 *                      already has. See migrateOffers.js.
 */
export function useOffers({ offerOutputs = {}, offersSeeded = false, markSeeded, enabled = false } = {}) {
  const { rows: offers, loading, error, save, remove, reload, clearError, newId } = useOfferRecords();

  /* The pull from the Accelerator. Her locked signature offer becomes the first
     rung without her re-typing it, so the ladder isn't an empty table on a
     foundation she's already built.

     `markSeeded` is only called when the save SUCCEEDED. If the write fails we
     must not record that the pull happened, or the offer is lost for good — the
     once-ever flag would block every retry. */
  const doPull = useCallback(async () => {
    const read = readSignatureOffer(offerOutputs);
    if (!read) return null;
    const saved = await save({
      id: newId(),
      type: "core",
      name: read.name || DEFAULT_CORE_NAME,
      summary: read.summary,
      pricing: read.pricing,
      tiedTo: "",
      status: "draft",
      source: "accelerator",
      sourceSection: SOURCE_SECTION,
      sourceText: read.sourceText,
      sourceHash: read.sourceHash,
    });
    if (saved && markSeeded) await markSeeded();
    return saved;
  }, [offerOutputs, save, newId, markSeeded]);

  /* The automatic once-ever pull.

     Four guards, each load-bearing:
       - `enabled` — settings have loaded and the localStorage migration has been
         attempted. Without it the pull races the migration and duplicates.
       - `loading` — pulling before the first load returns would create a second
         copy of an offer that is already in the table.
       - `offersSeeded` — the once-ever flag.
       - `pulling` ref — effects can re-run while the await is still in flight;
         without this, two overlapping pulls both insert. */
  const pulling = useRef(false);
  useEffect(() => {
    if (!enabled || loading || offersSeeded || pulling.current) return;
    if (!hasSignatureOffer(offerOutputs)) return;
    // Belt and braces: if a pulled row is already here, the flag is simply out
    // of date. Record that and don't insert a second one.
    if (offers.some((o) => o.source === "accelerator")) {
      if (markSeeded) markSeeded();
      return;
    }
    pulling.current = true;
    doPull().finally(() => { pulling.current = false; });
  }, [enabled, loading, offersSeeded, offerOutputs, offers, doPull, markSeeded]);

  /**
   * Replace a pulled offer's fields with what's in My Offer now. Destructive by
   * design — the caller must confirm first. Her own additions that the pull
   * can't produce (type, tied-to, status) are kept, since re-reading the source
   * says nothing about them.
   */
  const resync = useCallback(async (offer) => {
    const read = readSignatureOffer(offerOutputs);
    if (!read) return null;
    return save({
      ...offer,
      name: read.name || offer.name,
      summary: read.summary,
      pricing: read.pricing,
      sourceText: read.sourceText,
      sourceHash: read.sourceHash,
    });
  }, [offerOutputs, save]);

  return {
    offers, loading, error, clearError,
    saveOffer: save,
    deleteOffer: remove,
    reload,
    newOfferId: newId,
    pullSignatureOffer: doPull,
    resyncOffer: resync,
  };
}
